/**
 * Client-side shapes for the parts of the v1 API that are *not* domain entities
 * (prompt.md §47).
 *
 * WHY normalisers exist here: several endpoints return raw storage rows
 * (`audit.list`, `notifications.list`, `runs.get` tool calls) because the server
 * deliberately does not leak its row shape into the protocol. The UI still needs
 * a stable shape it can render and test against, so the SDK owns exactly one
 * mapping function per such row instead of every screen re-deriving fields with
 * unchecked casts.
 */

import {
  type Approval,
  type ConversationBranch,
  type Credential,
  type FileId,
  type MemoryRecord,
  type MessageId,
  type PlanPricing,
  type Run,
  type RunId,
  type Timestamp,
  type UserId,
  unsafeId,
} from "@dayvise/domain";

/** `GET /v1/runs/:id` */
export interface RunDetail {
  readonly run: Run;
  readonly toolCalls: readonly ToolCallRecord[];
  readonly transitions: readonly RunTransitionRecord[];
  /** `null` when the run has never been checkpointed. */
  readonly checkpoint: RunCheckpointRecord | null;
}

export interface RunTransitionRecord {
  readonly from: string | null;
  readonly to: string;
  readonly sequence: number;
}

/** A tool call as recorded by the platform, as returned inside `RunDetail`. */
export interface ToolCallRecord {
  readonly toolCallId: string | null;
  readonly toolId: string;
  readonly status: string;
  readonly riskLevel: string;
  readonly arguments: unknown;
  readonly result: unknown;
  readonly createdAt?: string | null;
}

export interface RunCheckpointRecord {
  readonly checkpointId: string;
  readonly sequence: number;
  readonly document: unknown;
}

/** `POST /v1/conversations/:id/messages` */
export interface SendMessageResult {
  /** Null when the message was answered by a server slash command (no run). */
  readonly runId: RunId | null;
  readonly messageId: MessageId;
  /** Branch to select; a command's user audit entry may remain on its source branch. */
  readonly branchId?: string;
  /** Plain text restored by /undo for editing, never an automatic send. */
  readonly prefill?: string;
  /** "unknown" when no runtime was dispatched (server slash command). */
  readonly runtimeId: string;
  /** Sealed secret handles created with the message (ids + labels, never values). */
  readonly secrets?: readonly { readonly secretId: string; readonly label: string }[];
  /** Present only when a server slash command answered instead of an agent run. */
  readonly command?: { readonly name: string; readonly text: string };
}

export interface SendMessageOutcome {
  /** `null` when the server deduplicated a retry whose first call never finished. */
  readonly result: SendMessageResult | null;
  /** True when the API recognised the idempotency key instead of starting a run. */
  readonly deduplicated: boolean;
}

/** `GET /v1/conversations/:id` */
export interface ConversationDetail {
  readonly runs?: readonly Run[];
  readonly conversation: import("@dayvise/domain").Conversation;
  readonly branches: readonly ConversationBranch[];
}

/** `GET /v1/files` */
export interface FileSummary {
  readonly fileId: FileId;
  readonly path: string;
  readonly version: number;
  readonly mimeType: string;
  readonly size: number;
  readonly storageProvider: "structured" | "blob";
  readonly updatedAt: Timestamp;
}

/** Result of `GET /v1/files/:id`, which streams bytes rather than JSON. */
export interface DownloadContent {
  readonly mimeType: string;
  readonly filename: string;
  readonly bytes: ArrayBuffer;
}

export interface FileContent extends DownloadContent {
  readonly fileId: string;
}

/** `GET /v1/credentials` */
export interface CredentialList {
  readonly aiProviders: readonly Credential[];
  readonly databases: readonly Credential[];
  readonly blobs: readonly Credential[];
  /** Generic secrets a person explicitly saved for reuse from secure text input. */
  readonly secrets: readonly Credential[];
}

/** `POST /v1/credentials/:id/test` */
export interface ConnectionTestResult {
  readonly ok: boolean;
  readonly detail: string | null;
}

/** `GET /v1/curator` — owner-facing skill maintenance status. */
export interface CuratorSkillSummary {
  readonly skillId: string;
  readonly name: string;
  readonly version: string;
  readonly visibility: string;
  readonly source: string;
  readonly archivedAt: string | null;
  readonly updatedAt: string;
  readonly recordedVersions: number;
  readonly pendingCandidates: number;
  readonly requiredTools: readonly string[];
}

export interface CuratorOverview {
  readonly skills: readonly CuratorSkillSummary[];
  readonly pendingCandidates: number;
  readonly archived: number;
}

/** `GET /v1/skills/:id/versions` — recorded content revisions of one skill. */
export interface SkillVersionRecord {
  readonly workspaceId: string;
  readonly skillId: string;
  readonly version: string;
  readonly ownerUserId: string | null;
  readonly instructions: string;
  readonly contentHash: string;
  readonly source: string;
  readonly refinementId: string | null;
  readonly createdAt: string;
}

export type LearningRefinementState = "queued" | "running" | "candidate" | "applied" | "reverted" | "failed" | "quarantined";
export type LearningRefinementTargetKind = "memory" | "skill";

/** `GET /v1/learning/refinements` — one bounded learning job and its candidate revision. */
export interface LearningRefinement {
  readonly workspaceId: string;
  readonly refinementId: string;
  readonly ownerUserId: string;
  readonly targetKind: LearningRefinementTargetKind;
  readonly targetId: string | null;
  readonly baseRevision: number | null;
  readonly sourceConversationId: string | null;
  readonly sourceMessageIds: readonly string[];
  readonly reason: string;
  readonly previousContent: string;
  readonly previousRevision: number | null;
  readonly proposal: string;
  readonly state: LearningRefinementState;
  readonly resultRevision: number | null;
  readonly error: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly decidedAt: string | null;
}

/** `GET /v1/usage` */
export interface UsageRollup {
  readonly since: Timestamp;
  readonly meters: readonly { readonly meter: string; readonly total: number }[];
  /** Current retained-storage gauge; `reconciled` false means accounting is still uninitialized. */
  readonly storage?: {
    readonly retainedBytes: number;
    readonly retainedCount: number;
    readonly reservedBytes: number;
    readonly reservedCount: number;
    readonly limitBytes: number | null;
    readonly reconciled: boolean;
    readonly revision: number;
  };
}

/**
 * `GET /v1/entitlements`. The server resolves plan, grants and policy into the
 * handful of answers the UI actually renders, so the client never re-derives
 * entitlement rules (prompt.md §5).
 */
export interface EntitlementsSummary {
  readonly plan: string;
  readonly pricing?: PlanPricing;
  /** Resolved entitlement decisions; the API returns the value and its source. */
  readonly byok: boolean | { readonly allowed: boolean };
  readonly byoDatabase: boolean | { readonly allowed: boolean };
  readonly byoBlob: boolean | { readonly allowed: boolean };
  readonly privateNetworking?: boolean | { readonly allowed: boolean };
  readonly sharedMemory?: boolean | { readonly allowed: boolean };
  readonly maxConcurrentRuns: number | null;
  /** Monthly managed-model token allowance; `null` means unlimited. */
  readonly monthlyManagedAiTokens?: number | null;
  readonly monthlyContainerMs?: number | null;
  readonly monthlyCpuMs?: number | null;
  readonly storageBytes: number | null;
  readonly maxSchedules?: number | null;
  readonly maxAgents?: number | null;
  readonly maxTeamMembers?: number | null;
  readonly maxRetentionDays?: number | null;
}

/** Both the decision object and the bare boolean are accepted. */
export function entitlementAllowed(value: boolean | { readonly allowed: boolean } | undefined): boolean {
  if (value === undefined) return false;
  return typeof value === "boolean" ? value : value.allowed;
}

/** A notification as delivered to one user (prompt.md §78). */
export interface NotificationRecord {
  readonly notificationId: string;
  readonly userId: UserId;
  readonly runId: RunId | null;
  readonly kind: string;
  readonly title: string;
  readonly body: string | null;
  readonly platforms: readonly string[];
  readonly createdAt: Timestamp;
}

/** An audit entry as exposed to admins (prompt.md §35, §70). */
export interface AuditEntry {
  readonly auditEventId: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly actorPrincipalId: string;
  readonly metadata: Readonly<Record<string, string | number | boolean | null>>;
  readonly createdAt: Timestamp;
}

export interface ApprovalList {
  readonly approvals: readonly Approval[];
}

export interface MemoryList {
  readonly private: readonly MemoryRecord[];
  readonly shared: readonly MemoryRecord[];
}

/* ------------------------------- normalisers ------------------------------ */

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function normalizeAuditEntry(raw: unknown): AuditEntry | null {
  const record = asRecord(raw);
  if (!record) return null;
  const auditEventId = readString(record, "auditEventId");
  const action = readString(record, "action");
  const createdAt = readString(record, "createdAt");
  if (!auditEventId || !action || !createdAt) return null;
  const metadata = asRecord(record.metadata) ?? {};
  const safeMetadata: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      safeMetadata[key] = value;
    }
  }
  return {
    auditEventId,
    action,
    targetType: readString(record, "targetType") ?? "unknown",
    targetId: readString(record, "targetId"),
    actorPrincipalId: readString(record, "actorPrincipalId") ?? "unknown",
    metadata: safeMetadata,
    createdAt,
  };
}

export function normalizeNotification(raw: unknown): NotificationRecord | null {
  const record = asRecord(raw);
  if (!record) return null;
  const notificationId = readString(record, "notificationId");
  const userId = readString(record, "userId");
  const createdAt = readString(record, "createdAt");
  if (!notificationId || !userId || !createdAt) return null;
  const runId = readString(record, "runId");
  const platforms = Array.isArray(record.platforms) ? record.platforms.filter((entry): entry is string => typeof entry === "string") : [];
  return {
    notificationId,
    userId: unsafeId<"user">(userId),
    runId: runId ? unsafeId<"run">(runId) : null,
    kind: readString(record, "kind") ?? "run",
    title: readString(record, "title") ?? "Dayvise",
    body: readString(record, "body"),
    platforms,
    createdAt,
  };
}

export function normalizeToolCall(raw: unknown): ToolCallRecord | null {
  const record = asRecord(raw);
  if (!record) return null;
  const toolId = readString(record, "toolId");
  if (!toolId) return null;
  return {
    toolCallId: readString(record, "toolCallId"),
    toolId,
    status: readString(record, "status") ?? "unknown",
    riskLevel: readString(record, "riskLevel") ?? "low",
    arguments: record.arguments ?? null,
    result: record.result ?? null,
    createdAt: readString(record, "createdAt"),
  };
}

export function normalizeRunTransition(raw: unknown): RunTransitionRecord | null {
  const record = asRecord(raw);
  if (!record) return null;
  const to = readString(record, "to");
  if (!to) return null;
  const from = readString(record, "from");
  return { from, to, sequence: readNumber(record, "sequence") ?? 0 };
}

export function normalizeRunCheckpoint(raw: unknown): RunCheckpointRecord | null {
  const record = asRecord(raw);
  if (!record) return null;
  const checkpointId = readString(record, "checkpointId");
  if (!checkpointId) return null;
  return { checkpointId, sequence: readNumber(record, "sequence") ?? 0, document: record.document ?? null };
}

/**
 * `POST /v1/conversations/:id/messages` answers with the run it started, or with
 * a `{ deduplicated, result }` envelope on an idempotent retry. Both collapse to
 * one shape here so callers cannot forget the second case.
 */
export function normalizeSendMessage(raw: unknown): SendMessageOutcome {
  const record = asRecord(raw);
  if (!record) return { result: null, deduplicated: false };
  if (record.deduplicated === true) {
    return { result: normalizeSendMessageResult(record.result), deduplicated: true };
  }
  return { result: normalizeSendMessageResult(record), deduplicated: false };
}

function normalizeSendMessageResult(raw: unknown): SendMessageResult | null {
  const record = asRecord(raw);
  if (!record) return null;
  const messageId = readString(record, "messageId");
  if (!messageId) return null;
  const runId = readString(record, "runId");
  const branchId = readString(record, "branchId");
  const commandRecord = asRecord(record.command);
  const commandName = commandRecord ? readString(commandRecord, "name") : null;
  const commandText = commandRecord ? readString(commandRecord, "text") : null;
  const secretsRaw = Array.isArray(record.secrets) ? record.secrets : [];
  const secrets = secretsRaw
    .map((entry) => {
      const item = asRecord(entry);
      const secretId = item ? readString(item, "secretId") : null;
      const label = item ? readString(item, "label") : null;
      return secretId && label ? { secretId, label } : null;
    })
    .filter((entry) => entry !== null);
  return {
    runId: runId ? unsafeId<"run">(runId) : null,
    messageId: unsafeId<"message">(messageId),
    ...(branchId ? { branchId } : {}),
    ...(typeof record.prefill === "string" ? { prefill: record.prefill } : {}),
    runtimeId: readString(record, "runtimeId") ?? "unknown",
    secrets,
    ...(commandName && commandText ? { command: { name: commandName, text: commandText } } : {}),
  };
}

export function normalizeFileSummary(raw: unknown): FileSummary | null {
  const record = asRecord(raw);
  if (!record) return null;
  const fileId = readString(record, "fileId");
  const path = readString(record, "path");
  if (!fileId || !path) return null;
  const provider = readString(record, "storageProvider");
  return {
    fileId: unsafeId<"file">(fileId),
    path,
    version: readNumber(record, "version") ?? 1,
    mimeType: readString(record, "mimeType") ?? "application/octet-stream",
    size: readNumber(record, "size") ?? 0,
    storageProvider: provider === "blob" ? "blob" : "structured",
    updatedAt: readString(record, "updatedAt") ?? new Date(0).toISOString(),
  };
}

export function normalizeConnectionTest(raw: unknown): ConnectionTestResult {
  const record = asRecord(raw) ?? {};
  return { ok: record.ok === true, detail: readString(record, "detail") };
}

/** Summary of a delegated child subagent execution. */
export interface SubagentDelegationSummary {
  readonly delegationId: string;
  readonly subRunId: string;
  readonly parentRunId?: string;
  readonly status: "running" | "completed" | "failed" | string;
  readonly task: string;
  readonly background: boolean;
  readonly worktree: "branch" | "inherit";
  readonly worktreePath?: string | null;
  readonly output?: string;
  readonly text?: string;
  readonly iterations?: number;
  readonly usage?: {
    readonly inputTokens: number;
    readonly outputTokens: number;
    readonly totalTokens: number;
  };
  readonly startedAt?: string;
  readonly completedAt?: string | null;
  readonly error?: string | null;
}

export interface ClarificationQuestion {
  readonly qid: string;
  readonly id?: string | null;
  readonly question: string;
  readonly choices?: readonly string[] | null;
  readonly choices_offered?: readonly string[] | null;
  readonly multi_select?: boolean;
}

export interface ClarificationRequest {
  readonly clarificationId: string;
  readonly runId: string;
  readonly questions: readonly ClarificationQuestion[];
}

export interface ClarificationAnswerOutcome {
  readonly ok: boolean;
  readonly clarificationId: string;
  readonly sequence: number;
}

export interface LearningEvent {
  readonly eventId: string;
  readonly kind: string;
  readonly sourceConversationId: string | null;
  readonly sourceMessageId: string | null;
  readonly runId: string | null;
  readonly summary: string;
  readonly status: string;
  readonly revision: number;
  readonly createdAt: Timestamp;
  readonly updatedAt: Timestamp;
}

export interface WorkspaceSnapshotView {
  readonly snapshotId: string;
  readonly formatVersion: number;
  readonly sha256: string;
  readonly bytes: number;
  readonly agents: number;
  readonly skills: number;
  readonly createdAt: Timestamp;
  readonly agentNames: readonly string[];
  readonly skillNames: readonly string[];
}

export interface SnapshotRestoreResult {
  readonly restored: true;
  readonly snapshotId: string;
  readonly agents: number;
  readonly skills: number;
}

export interface AutomationTemplateParameterView {
  readonly key: string;
  readonly label: string;
  readonly required: boolean;
}

export interface AutomationTemplateView {
  readonly templateId: string;
  readonly name: string;
  readonly version: string;
  readonly formatVersion: number;
  readonly prompt: string;
  readonly cronExpression: string | null;
  readonly runAt: string | null;
  readonly timezone: string;
  readonly parameters: readonly AutomationTemplateParameterView[];
  readonly createdAt: Timestamp;
  readonly updatedAt: Timestamp;
}

export interface TemplateUseResult {
  readonly scheduleId: string;
  readonly prompt: string;
  readonly templateId: string;
}

export interface SkillBundleView {
  readonly bundleId: string;
  readonly name: string;
  readonly formatVersion: number;
  readonly sha256: string;
  readonly bytes: number;
  readonly skills: number;
  readonly createdAt: Timestamp;
  readonly skillNames: readonly string[];
}

export interface BundleInstallPreview {
  readonly created: readonly string[];
  readonly updated: readonly string[];
  readonly expectedVersions: Readonly<Record<string, string | null>>;
}

export interface SkillShareView {
  readonly shareId: string;
  readonly ownerUserId: string;
  readonly sourceWorkspaceId: string;
  readonly sourceSkillId: string;
  readonly targetWorkspaceId: string;
  readonly sourceVersion: string;
  readonly document: { readonly name: string; readonly version: string; readonly instructions: string; readonly frontmatter: Record<string, unknown> | null; readonly platforms: readonly string[]; readonly tags: readonly string[]; readonly category: string | null; readonly source: string };
  readonly state: "pending" | "accepted" | "revoked";
  readonly followUpdates: boolean;
  readonly targetSkillId: string | null;
  readonly targetVersion: string | null;
  readonly acceptedAt: string | null;
  readonly revokedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly updateAvailable?: boolean;
}

export interface SkillShareTargetView {
  readonly workspaceId: string;
  readonly name: string;
  readonly kind: string;
}

export interface OutputRestorePreview {
  readonly path: string;
  readonly action: "create" | "overwrite" | "unchanged";
  readonly current: { readonly version: number; readonly hash: string; readonly size: number; readonly mimeType: string } | null;
  readonly target: { readonly snapshotFileId: string; readonly revision: number; readonly hash: string; readonly size: number; readonly mimeType: string; readonly runId: string | null; readonly branchId: string | null };
}

export interface OutputRestoreResult {
  readonly restored: true;
  readonly path: string;
  readonly version: number;
  readonly from: { readonly snapshotFileId: string; readonly revision: number; readonly runId: string | null; readonly branchId: string | null };
}

